import type {
  PaymentContextFailureDTO,
  PaymentContextSuccessDTO,
  PaymentContextVerifyDTO,
  PaymentMaybePayloadDTO,
  PaymentRequestResponseDTO,
  PaymentRequestTemplateDTO,
  PaymentVerifyDTO,
  PayViaLinkDetailsDTO,
} from '../dto/payment.dto';
import type {
  PaymentContextFailureModel,
  PaymentContextSuccessModel,
  PaymentContextVerifyModel,
  PaymentRequestModel,
  PaymentResult,
  PayViaLinkDetailsModel,
} from '../../domain/models/payment.types';

export const adaptPayViaLinkDetailsDtoToModel = (dto: PayViaLinkDetailsDTO): PayViaLinkDetailsModel => {
  const orderId = dto.orderId || dto.txnId || '';
  const razorpayKey = dto.razorpayKeyId || dto.key || '';
  const expiresAt = dto.paymentDetails?.expiresAt;

  return {
    source: dto.source,
    amount: dto.amount,
    currency: dto.currency || 'INR',
    orderId,
    txnId: dto.txnId,
    merchantName: dto.merchantName || 'BlueStone',
    totalItems: dto.totalItems ?? 1,
    savings: dto.savings ?? 0,
    key: razorpayKey,
    razorpayKeyId: dto.razorpayKeyId,
    razorpayCustomerId: dto.razorpayCustomerId,
    isBankOfferSelected: dto.paymentDetails?.isBankOfferSelected === true,
    paymentDetails: dto.paymentDetails,
    expiresAt,
  };
};

const unwrapPaymentPayload = <T>(rawResponse: PaymentMaybePayloadDTO<T>): T => {
  return rawResponse && typeof rawResponse === 'object' && 'payload' in rawResponse
    ? (rawResponse.payload as T)
    : (rawResponse as T);
};

export const adaptPayViaLinkDetailsResponseDtoToModel = (
  rawResponse: PaymentMaybePayloadDTO<PayViaLinkDetailsDTO>
): PayViaLinkDetailsModel => {
  const response = unwrapPaymentPayload(rawResponse);

  if (response.hasError) {
    throw new Error(response.message || 'Unable to load payment details.');
  }

  return adaptPayViaLinkDetailsDtoToModel(response);
};

const parsePaymentRequestTemplate = (
  rawResponse: PaymentRequestResponseDTO
): PaymentRequestTemplateDTO | undefined => {
  if (typeof rawResponse?.data === 'string') {
    return JSON.parse(rawResponse.data) as PaymentRequestTemplateDTO;
  }

  return rawResponse?.data;
};

export const adaptPaymentRequestDtoToModel = (
  rawResponse: PaymentRequestResponseDTO
): PaymentRequestModel => {
  if (rawResponse?.hasError) {
    throw new Error(rawResponse.errorMsg || String(rawResponse.data || 'Unable to create payment request.'));
  }

  if (!rawResponse?.data && rawResponse?.message) {
    throw new Error(rawResponse.message);
  }

  const parsed = parsePaymentRequestTemplate(rawResponse);
  const templateVariables = parsed?.templateVariables || {};
  const amount = Number(templateVariables.amount);
  const customerPhone = String(templateVariables.customerPhone || '');
  const customerEmail = String(templateVariables.customerEmail || '');
  const currency = String(templateVariables.currency || 'INR');
  const callbackUrl = String(templateVariables.callbackURL || '');
  const razorpayOrderId = String(templateVariables.razorpayOrderId || '');
  const razorpayOfferId = templateVariables.razorpayOfferId
    ? String(templateVariables.razorpayOfferId)
    : undefined;

  if (!amount || !callbackUrl || !razorpayOrderId) {
    throw new Error('Payment request details are incomplete. Please try again.');
  }

  return {
    amount,
    customerPhone,
    customerEmail,
    currency,
    callbackUrl,
    razorpayOrderId,
    razorpayOfferId,
    isVmTemplate: Boolean(parsed?.isVmTemplate),
  };
};

export const adaptPaymentContextVerifyDtoToModel = (
  rawResponse: PaymentMaybePayloadDTO<PaymentContextVerifyDTO>
): PaymentContextVerifyModel => {
  const payload = unwrapPaymentPayload(rawResponse);
  const expiresAt = Number(payload?.expiresAt || 0);

  return {
    paymentStatusStr: String(payload?.paymentStatusStr || 'PENDING'),
    txnId: String(payload?.txnId || ''),
    paymentSource: payload?.paymentSource ? String(payload.paymentSource) : undefined,
    ...(expiresAt ? { expiresAt } : {}),
  };
};

export const adaptPaymentContextSuccessDtoToModel = (
  rawResponse: PaymentMaybePayloadDTO<PaymentContextSuccessDTO>
): PaymentContextSuccessModel => {
  const payload = unwrapPaymentPayload(rawResponse);

  return {
    txnId: String(payload?.txnId || ''),
    amount: Number(payload?.amount || 0),
    paymentSuccessTime: Number(payload?.paymentSuccessTime || Date.now()),
    message: payload?.message,
  };
};

export const adaptPaymentContextFailureDtoToModel = (
  rawResponse: PaymentMaybePayloadDTO<PaymentContextFailureDTO>
): PaymentContextFailureModel => {
  const payload = unwrapPaymentPayload(rawResponse);

  return {
    txnId: String(payload?.txnId || ''),
    errorCode: payload?.errorCode ?? null,
    errorDesc: payload?.errorDesc ?? null,
    paymentMethod: payload?.paymentMethod ?? null,
    wallet: payload?.wallet ?? null,
  };
};

export const adaptPaymentVerifyDtoToResult = (
  rawResponse: PaymentMaybePayloadDTO<PaymentVerifyDTO>
): PaymentResult => {
  const payload = unwrapPaymentPayload(rawResponse);

  return {
    success: Boolean(payload?.success),
    transactionId: payload?.transactionId,
  };
};
